import { vehiclePrices } from '../vehiclePrices';
import React from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Link,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import sealionHero from '../images/sealion-7.jpg';
import PageSeo, {
  articleJsonLd,
  breadcrumbJsonLd,
  keywordSets,
  productJsonLd,
} from '../PageSeo';

function Sealion7Detail() {
  const currentPrice = vehiclePrices.sealion7[0].priceValue;
  const formattedStartingPrice = vehiclePrices.sealion7[0].price;

  const pageTitle = `BYD Sealion 7 Indonesia 2026 | Harga ${formattedStartingPrice}`;
  const pageDescription = `Cek harga BYD Sealion 7 mulai ${formattedStartingPrice} di BYD BSD Serpong. Satu varian NIK 2026 dengan baterai baru. Konsultasi pembelian dan test drive bersama Junius.`;

  return (
    <Box
      sx={{ padding: { xs: 2, sm: 4 }, maxWidth: '1200px', margin: '0 auto' }}
    >
      <PageSeo
        title={pageTitle}
        description={pageDescription}
        keywords={[
          ...keywordSets.localDealer,
          'BYD Sealion 7 Indonesia',
          'harga BYD Sealion 7',
          'BYD Sealion 7 baterai baru 2026',
          'harga BYD Sealion 7 879 juta',
          'Sealion 7 BSD Serpong',
        ]}
        path='/blog/sealion-7-suv-listrik-sporty'
        image={sealionHero}
        type='article'
        jsonLd={[
          productJsonLd({
            name: 'BYD Sealion 7',
            description: pageDescription,
            image: sealionHero,
            path: '/blog/sealion-7-suv-listrik-sporty',
            lowPrice: currentPrice,
            highPrice: currentPrice,
            offerCount: 1,
          }),
          articleJsonLd({
            title: pageTitle,
            description: pageDescription,
            image: sealionHero,
            path: '/blog/sealion-7-suv-listrik-sporty',
          }),
          breadcrumbJsonLd([
            { name: 'Beranda', path: '/' },
            { name: 'Blog', path: '/blog' },
            {
              name: 'BYD Sealion 7',
              path: '/blog/sealion-7-suv-listrik-sporty',
            },
          ]),
        ]}
      />

      <Box sx={{ mt: 10 }}>
        {/* H1: Judul Utama SEO */}
        <Typography
          variant='h3'
          component='h1'
          fontWeight='bold'
          gutterBottom
          sx={{ fontSize: { xs: '2rem', md: '3rem' } }}
        >
          BYD Sealion 7 NIK 2026: Baterai Baru, Harga Rp879 Juta
        </Typography>

        {/* Satu-Satunya Gambar Hero */}
        <Box
          component='img'
          src={sealionHero}
          alt='BYD Sealion 7 SUV listrik di BSD Serpong'
          sx={{
            width: '100%',
            height: 'auto',
            borderRadius: 2,
            mb: 4,
            boxShadow: 3,
          }}
        />

        <Typography
          variant='body1'
          paragraph
          sx={{ fontSize: '1.1rem', lineHeight: 1.8 }}
        >
          <strong>BYD Sealion 7</strong> hadir di Indonesia sebagai standar baru
          di kelas Sport SUV listrik. Memadukan kemewahan interior dengan
          performa yang menakjubkan, Sealion 7 ditujukan bagi Anda yang
          menginginkan kecepatan tanpa mengorbankan fungsionalitas keluarga.
          Bagi pemilik di wilayah Jabodetabek, Sealion 7 memberikan privilese{' '}
          <strong>Bebas Ganjil Genap</strong> di jalur protokol Jakarta serta
          keuntungan finansial berupa{' '}
          <strong>Pajak Kendaraan Listrik Murah</strong> yang sangat rendah
          setiap tahunnya.
        </Typography>

        {/* H2: Tabel Perbandingan Varian */}
        <Typography
          variant='h4'
          component='h2'
          fontWeight='bold'
          sx={{ mt: 5, mb: 3 }}
        >
          Harga Sealion 7 — Satu Varian NIK 2026
        </Typography>

        <TableContainer component={Paper} elevation={3} sx={{ mb: 4 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Informasi</TableCell>
                <TableCell>Sealion 7 NIK 2026</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell>Harga OTR</TableCell>
                <TableCell>{formattedStartingPrice}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>NIK</TableCell>
                <TableCell>2026</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Baterai</TableCell>
                <TableCell>Baterai baru</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Pilihan varian</TableCell>
                <TableCell>1 varian</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>

        {/* Konten SEO Padat Teks */}
        <Grid container spacing={4} sx={{ mt: 2 }}>
          <Grid item xs={12} md={6}>
            <Typography
              variant='h5'
              component='h3'
              fontWeight='bold'
              gutterBottom
            >
              Satu Varian untuk NIK 2026
            </Typography>
            <Typography variant='body1' paragraph>
              Sealion 7 NIK 2026 tersedia satu varian dengan harga
              <strong> {formattedStartingPrice}</strong>. Hubungi Junius untuk
              penawaran pembelian, ketersediaan unit, dan jadwal test drive.
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography
              variant='h5'
              component='h3'
              fontWeight='bold'
              gutterBottom
            >
              Baterai Baru NIK 2026
            </Typography>
            <Typography variant='body1' paragraph>
              Unit NIK 2026 menggunakan baterai baru. Kapasitas, jarak tempuh,
              dan kemampuan pengisian daya untuk unit yang dipesan dapat
              dikonsultasikan dengan Junius sebelum pembelian.
            </Typography>
          </Grid>
        </Grid>

        {/* Local SEO & BSD Context */}
        <Typography
          variant='h5'
          component='h3'
          fontWeight='bold'
          sx={{ mt: 5, mb: 2 }}
        >
          Dukungan Ekosistem Charging Station BSD
        </Typography>
        <Typography variant='body1' paragraph>
          Mengendarai BYD Sealion 7 di area Tangerang Selatan semakin nyaman
          berkat masifnya titik <strong>Charging Station BSD</strong>. Mulai
          dari SPKLU di pusat perbelanjaan hingga fasilitas pengisian daya cepat
          di dealer <strong>BYD BSD Serpong</strong>. Teknologi Blade Battery
          yang digunakan juga mendukung pengisian daya cepat, sehingga Anda
          tidak perlu menunggu lama untuk kembali beraktivitas.
        </Typography>

        {/* Internal Linking */}
        <Paper
          variant='outlined'
          sx={{ p: 3, my: 5, backgroundColor: '#f9f9f9' }}
        >
          <Typography variant='h6' fontWeight='bold' gutterBottom>
            Eksplorasi Produk BYD Lainnya:
          </Typography>
          <Typography variant='body2' sx={{ lineHeight: 2 }}>
            • Butuh MPV Keluarga? Cek{' '}
            <Link component={RouterLink} to='/blog/byd-m6-mpv-listrik-keluarga'>
              Harga & Spesifikasi BYD M6
            </Link>
            .
            <br />• Mobil Listrik Paling Value for Money:{' '}
            <Link
              component={RouterLink}
              to='/blog/byd-atto-3-advanced-plus-harga-spesifikasi'
            >
              Update BYD Atto 3 Advanced Plus
            </Link>
            .
            <br />• Lihat daftar harga lengkap di{' '}
            <Link component={RouterLink} to='/pricing'>
              Price List BYD BSD 2026
            </Link>
            .
          </Typography>
        </Paper>

        {/* CTA */}
        <Box
          sx={{
            textAlign: 'center',
            py: 5,
            backgroundColor: '#1a1a1a',
            color: '#fff',
            borderRadius: 2,
          }}
        >
          <Typography
            variant='h4'
            component='h2'
            fontWeight='bold'
            gutterBottom
          >
            Rasakan Torsi 690 Nm Sealion 7!
          </Typography>
          <Typography variant='body1' sx={{ mb: 4, px: 2 }}>
            Booking test drive sekarang di BSD Serpong dan dapatkan hitungan
            cicilan khusus bunga rendah bersama Junius.
          </Typography>
          <Button
            variant='contained'
            size='large'
            href='https://wa.me/6281286864825'
            target='_blank'
            sx={{
              backgroundColor: '#25D366',
              fontWeight: 'bold',
              px: 6,
              py: 1.5,
              borderRadius: '25px',
              fontSize: '1.1rem',
              '&:hover': { backgroundColor: '#128c7e' },
            }}
          >
            Hubungi Junius via WhatsApp
          </Button>
        </Box>
      </Box>
    </Box>
  );
}

export default Sealion7Detail;
